<template>
    <div class="filter-page">
        <div class="filter-page-header">
            <div class="filter-header-content">

                <div class="filter-title-section">
                    <button class="back-button" @click="goBackToChat" :title="$t('chat.filterPanel.backToChat')">
                        <i class="material-icons">arrow_back</i>
                    </button>
                    <div class="filter-icon-container">
                        <div class="filter-icon editable">
                            <MaterialIconPicker :value="selectedIcon || 'filter_list'" @input="onIconSelected" />
                        </div>
                    </div>
                    <div class="filter-title-text">
                        <template v-if="!editingName">
                            <div class="filter-name-container editable">
                                <a-tooltip
                                    v-if="isCustomFilter"
                                    :title="filter.visibility === 'public' ? $t('chat.filterPanel.publicFilter') : $t('chat.filterPanel.privateFilter')"
                                >
                                    <i class="material-icons visibility-icon">
                                        {{ filter.visibility === "public" ? "public" : "lock" }}
                                    </i>
                                </a-tooltip>
                                <span class="filter-name">{{ displayFilterName }}</span>
                                <button class="edit-name-btn" @click="startEditName" :title="$t('chat.filterPanel.editFilterName')">
                                    <i class="material-icons">edit</i>
                                </button>
                            </div>
                            <div class="contact-count" v-if="totalContacts !== null">
                                <span class="count-text">{{ totalContacts || 0 }}{{ $t('chat.filterPanel.contactsFound') }}</span>
                            </div>
                        </template>
                        <template v-else>
                            <div class="edit-name-container">
                                <input
                                    ref="nameInput"
                                    class="filter-name-input"
                                    type="text"
                                    :value="displayFilterName"
                                    @input="onNameInput"
                                    @keyup.enter="commitEditName"
                                    @blur="commitEditName"
                                    :placeholder="$t('chat.filterPanel.enterFilterName')"
                                />
                            </div>
                        </template>
                    </div>
                </div>

                <div class="filter-actions" v-if="showSaveButton || isCustomFilter || true">
                    <SupsisButton @click="openFilterSuggestionsModal" class="filter-suggestions-btn">
                        <i class="material-icons">auto_awesome</i>
                        {{ $t('chat.filterPanel.filterSuggestions') }}
                    </SupsisButton>
                    <div class="visibility-selector" v-if="isSystemFilter">
                        <label class="visibility-option" :class="{ active: filterVisibility === 'private' }">
                            <input type="radio" name="visibility" value="private" v-model="filterVisibility" />
                            <i class="material-icons">lock</i>
                            <span>{{ $t('chat.filterPanel.private') }}</span>
                        </label>
                        <label class="visibility-option" :class="{ active: filterVisibility === 'public' }">
                            <input type="radio" name="visibility" value="public" v-model="filterVisibility" />
                            <i class="material-icons">public</i>
                            <span>{{ $t('chat.filterPanel.public') }}</span>
                        </label>
                    </div>
                    <SupsisButton v-if="showSaveButton" primary @click="onSaveFilter" class="save-btn">
                        <i class="material-icons">save</i>
                        {{ isSystemFilter ? $t('chat.filterPanel.createFilter') : $t('chat.filterPanel.update') }}
                    </SupsisButton>
                    <CustomPopover
                        v-if="isCustomFilter"
                        :showIcon="false"
                        :showDropdownArrow="false"
                        popoverClass="filter-menu-popover"
                    >
                        <template #trigger>
                            <button class="filter-menu-btn">
                                <i class="material-icons">more_vert</i>
                            </button>
                        </template>
                        <template #content>
                            <div class="filter-menu-content">
                                <div
                                    class="filter-menu-item"
                                    @click="changeVisibility('public')"
                                    v-if="filter.visibility !== 'public'"
                                >
                                    <i class="material-icons">public</i>
                                    <span>{{ $t('chat.filterPanel.changeToPublic') }}</span>
                                </div>
                                <div
                                    class="filter-menu-item"
                                    @click="changeVisibility('private')"
                                    v-if="filter.visibility !== 'private'"
                                >
                                    <i class="material-icons">lock</i>
                                    <span>{{ $t('chat.filterPanel.changeToPrivate') }}</span>
                                </div>
                                <div class="filter-menu-divider"></div>
                                <div class="filter-menu-item danger" @click="deleteFilter">
                                    <i class="material-icons">delete</i>
                                    <span>{{ $t('chat.filterPanel.delete') }}</span>
                                </div>
                            </div>
                        </template>
                    </CustomPopover>
                </div>
            </div>
        </div>

        <div class="search-section">
            <div class="search-container">
                <div class="search-field-selector">
                    <MinimalPopoverSelect
                        v-model="selectedField"
                        :options="fieldOptions"
                        class="field-selector"
                        :show-dropdown-arrow="true"
                        :icon="'search'"
                    />
                </div>

                <div class="search-input-wrapper">
                    <input
                        class="search-input"
                        type="text"
                        :placeholder="placeholderText"
                        v-model="searchQuery"
                        @keyup.enter="searchContacts"
                    />
                </div>

                <div class="search-icon-wrapper">
                    <i class="material-icons search-icon" v-if="!searchQuery">search</i>
                    <button v-else class="clear-search-btn" @click="clearSearch" :title="$t('chat.filterPanel.clearSearch')">
                        <i class="material-icons">close</i>
                    </button>
                </div>
            </div>
        </div>

        <div class="filter-content-wrapper">
            <FiltersSection :filters="filters" :state="state" :sort="sortValue" :total="totalContacts"
                        @filters-changed="onFiltersChanged" />

            <div class="pagination-controls" v-if="contacts.length > 0">
                <SupsisButton
                    :disabled="!cursor.prevToken || loading"
                    @click="goToPrevPage"
                    class="pagination-btn prev-btn"
                >
                    <i class="material-icons">arrow_back</i>
                    {{ $t('chat.filterPanel.previous') }}
                </SupsisButton>
                <SupsisButton
                    :disabled="!cursor.nextToken || loading"
                    @click="goToNextPage"
                    class="pagination-btn next-btn"
                >
                    {{ $t('chat.filterPanel.next') }}
                    <i class="material-icons">arrow_forward</i>
                </SupsisButton>
            </div>
        </div>

        <Modal2
            v-if="showFilterSuggestionsModal"
            design="v3"
            :show-title="false"
            :show-footer="false"
            :xlarge="true"
            class-name="filter-suggestions-modal"
            @cancel="closeFilterSuggestionsModal"
        >
            <FilterSuggestionsModal
                @close="closeFilterSuggestionsModal"
                @apply-filters="applyFiltersFromSuggestions"
            />
        </Modal2>

        <FilterNameModal
            :visible="showFilterNameModal"
            :filter-name="pendingFilterName"
            :filter-description="pendingFilterDescription"
            :suggested-name="pendingSuggestedName"
            @confirm="confirmFilterName"
            @cancel="cancelFilterName"
            @update-suggested-name="updateSuggestedName"
        />

        <Modal2
            v-if="showSearchModeModal"
            design="v3"
            :show-title="true"
            :show-footer="true"
            class-name="search-mode-confirmation-modal"
            @ok="confirmSearchMode"
            @cancel="cancelSearchMode"
        >
            <template #title>
                <div class="search-mode-modal-title">
                    <i class="material-icons">{{ getSearchModeIcon() }}</i>
                    <span>{{ getSearchModeTitle() }}</span>
                </div>
            </template>
            <div class="search-mode-modal-content">
                <p>{{ getSearchModeMessage() }}</p>
                <div class="search-query-preview">
                    <strong>{{ $t('chat.filterPanel.searchTerm') }}</strong> {{ pendingSearchQuery }}
                </div>

                <!-- Mongo ID için özel seçim -->
                <div v-if="detectedSearchMode === 'mongoId'" class="mongo-id-selection">
                    <p class="selection-label">{{ $t('chat.filterPanel.whichFieldToSearch') }}</p>
                    <div class="id-type-buttons">
                        <SupsisButton
                            @click="selectMongoIdType('contactId')"
                            class="id-type-btn"
                            :primary="mongoIdSelectedType === 'contactId'"
                        >
                            <i class="material-icons">person</i>
                            {{ $t('chat.filterPanel.contactId') }}
                        </SupsisButton>
                        <SupsisButton
                            @click="selectMongoIdType('conversationId')"
                            class="id-type-btn"
                            :primary="mongoIdSelectedType === 'conversationId'"
                        >
                            <i class="material-icons">chat</i>
                            {{ $t('chat.filterPanel.conversationId') }}
                        </SupsisButton>
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="search-mode-modal-footer">
                    <SupsisButton @click="cancelSearchMode">
                        {{ $t('chat.filterPanel.noSearchAllFields') }}
                    </SupsisButton>
                    <SupsisButton
                        v-if="detectedSearchMode !== 'mongoId'"
                        primary
                        @click="confirmSearchMode"
                    >
                        {{ $t('chat.filterPanel.yesSearchOnlyField', { field: getSearchModeFieldName() }) }}
                    </SupsisButton>
                    <SupsisButton
                        v-else
                        primary
                        @click="confirmSearchMode"
                        :disabled="!mongoIdSelectedType"
                    >
                        {{ $t('chat.filterPanel.search') }}
                    </SupsisButton>
                </div>
            </template>
        </Modal2>

        <div class="contact-list-container">
            <ContactList
                :contacts="contacts"
                :loading="loading"
                :total="totalContacts"
                :filterable="true"
                :filters="tableFilters"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @filter-toggle="onFilterToggle"
                @sort-change="onSortChange"
            />
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import MinimalPopoverSelect from "./MinimalPopoverSelect.vue";
import MaterialIconPicker from "@/views/Pages/Module/MaterialIconPicker.vue";
import FiltersSection from "./FiltersSection.vue";
import ContactList from "./ContactList.vue";
import CustomPopover from "./CustomPopover.vue";
import Modal2 from "@/components/common/Modal2.vue";
import FilterSuggestionsModal from "./FilterSuggestionsModal.vue";
import FilterNameModal from "./FilterNameModal.vue";
import _ from "lodash";
import StringUtils from "@/utils/StringUtils";
import { createFilter, deleteFilter as deleteFilterApi, updateFilter, updateFilterMultipleKey } from "@/api/favFilters";

export default {
    name: "FilterPanel",
    props: {
        filterId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            contactTimer: null,
            state: "all",
            filters: [],
            contacts: [],
            totalContacts: 0,
            loading: false,
            editingName: false,
            tempFilterName: "",
            selectedField: "all",
            searchQuery: "",
            tempFilterIcon: "",
            cursor: {
                limit: 20,
                nextToken: null,
                prevToken: null,
                firstPageToken: null,
            },
            sortValue: "relevance",
            sortField: null,
            sortDirection: null,
            filterVisibility: "private",
            showFilterSuggestionsModal: false,
            showSearchModeModal: false,
            detectedSearchMode: null,
            pendingSearchQuery: null,
            mongoIdSelectedType: null,
            showFilterNameModal: false,
            pendingFilterName: "",
            pendingFilterDescription: "",
            pendingSuggestedName: "",
            pendingFilterData: null,
        };
    },
    components: { SupsisButton, MinimalPopoverSelect, MaterialIconPicker, FiltersSection, ContactList, CustomPopover, Modal2, FilterSuggestionsModal, FilterNameModal },
    computed: {
        ...mapState({
            contactFilters: (state) => state.contact.filters,
        }),
        fieldOptions() {
            return [
                { label: this.$t('chat.filterPanel.allFields'), value: "all", icon: "search" },
                { label: this.$t('chat.filterPanel.fullname'), value: "fullname", icon: "person" },
                { label: this.$t('chat.filterPanel.email'), value: "email", icon: "email" },
                { label: this.$t('chat.filterPanel.whatsappNumber'), value: "whatsappNumber", icon: "comment" },
                { label: this.$t('chat.filterPanel.phone'), value: "phone", icon: "phone" },
                { label: this.$t('chat.filterPanel.contactIdField'), value: "contactId", icon: "person" },
                { label: this.$t('chat.filterPanel.conversationIdField'), value: "conversationId", icon: "chat" },
            ];
        },
        tableFilters() {
            // filters'ı NewSupsisTable formatına çevir
            return this.transformFiltersToTable(this.filters);
        },
        filter() {
            return this.contactFilters.find((filter) => {
                const id = filter && (filter._id || filter.id);
                return id === this.filterId;
            });
        },
        isSystemFilter() {
            return this.filter?.type === "system";
        },
        isCustomFilter() {
            return this.filter?.type === "custom";
        },
        filterName() {
            return this.filter ? this.filter.name : "All";
        },
        placeholderText() {
            const labels = {
                all: "Tüm alanlarda aramak istediğinizi buraya yazın",
                fullname: "Ad Soyad ile aramak istediğinizi buraya yazın",
                email: "Email ile aramak istediğinizi buraya yazın",
                whatsappNumber: "WhatsApp ile aramak istediğinizi buraya yazın",
                phone: "Telefon ile aramak istediğinizi buraya yazın",
                contactId: "Aramak istediğiniz kişinin ID'sini buraya yazın",
                conversationId: "Aramak istediğiniz sohbetin ID'sini buraya yazın",
            };
            return labels[this.selectedField] || "Ara";
        },
        selectedIcon() {
            return this.tempFilterIcon || this.filter?.icon || "filter_list";
        },
        currentFiltersHash() {
            const normalized = this.normalizeFilters(this.filters);
            const str = JSON.stringify(normalized);
            return StringUtils.hashCodeStr(str);
        },
        savedFiltersHash() {
            const saved = (this.filter && this.filter.filters) || [];
            const normalized = this.normalizeFilters(saved);
            const str = JSON.stringify(normalized);
            return StringUtils.hashCodeStr(str);
        },
        isFiltersChanged() {
            return this.currentFiltersHash !== this.savedFiltersHash;
        },
        displayFilterName() {
            let name = this.filter ? this.filter.name : "All";
            if (this.isSystemFilter && this.isFiltersChanged) name = name + this.$t('chat.filterPanel.edited');
            if (this.hasNameChanged) {
                name = this.tempFilterName || this.$t('chat.filterPanel.unnamedFilter');
            }
            return name;
        },
        showSaveButton() {
            return this.isFiltersChanged || this.hasNameChanged || this.hasIconChanged;
        },
        hasNameChanged() {
            if (!this.tempFilterName || this.tempFilterName === "") {
                return false;
            }
            if (this.tempFilterName === null && this.filter?.name) {
                return true;
            }
            return this.tempFilterName !== this.filter?.name;
        },
        hasIconChanged() {
            if (!this.tempFilterIcon || this.tempFilterIcon === "") {
                return false;
            }
            return this.tempFilterIcon !== this.filter?.icon;
        },
    },
    watch: {
        selectedField(newValue) {
            this.getContactDebounced(20, true, undefined, true);
        },
        filter(newValue, oldValue) {
            if (newValue && oldValue && newValue.id === oldValue.id) return;
            this.tempFilterName = "";
            this.tempFilterIcon = "";
            this.initFilters();
            if (this.filterId) {
                this.getContactDebounced(20, true, undefined, true);
            }
        },
    },
    methods: {
        ...mapActions({
            fetchContacts: "contact/fetchContacts",
            updateContactListFilters: "contact/updateContactListFilters",
            setActiveView: "contact/setActiveView",
        }),
        createNameKey(name) {
            return name
                .toLowerCase()
                .replace(/ğ/g, "g")
                .replace(/ü/g, "u")
                .replace(/ş/g, "s")
                .replace(/ı/g, "i")
                .replace(/ö/g, "o")
                .replace(/ç/g, "c")
                .replace(/[^a-z0-9\s]/g, "")
                .replace(/^[0-9]+/, "")
                .trim()
                .split(/\s+/)
                .filter(word => word.length > 0)
                .map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
                .join("");
        },
        getUniqueFilterName(baseName) {
            const baseNameKey = this.createNameKey(baseName);
            let uniqueName = baseName;
            let nameKey = baseNameKey;

            const allFilters = this.contactFilters || [];
            const existingNameKeys = new Set();
            const existingFilters = [];

            allFilters.forEach(filter => {
                let filterNameKey = null;
                if (filter.type === "custom" && filter.key) {
                    filterNameKey = filter.key;
                    existingNameKeys.add(filterNameKey);
                    existingFilters.push({ nameKey: filterNameKey, name: filter.name });
                } else if (filter.type === "system" && filter.name) {
                    filterNameKey = this.createNameKey(filter.name);
                    existingNameKeys.add(filterNameKey);
                    existingFilters.push({ nameKey: filterNameKey, name: filter.name });
                }
            });

            if (existingNameKeys.has(nameKey)) {
                const baseNameRegex = new RegExp(`^${baseNameKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(Kopya\\d*)?$`);

                let maxCloneNumber = 0;
                existingFilters.forEach(filter => {
                    if (filter.nameKey && baseNameRegex.test(filter.nameKey)) {
                        const cloneMatch = filter.nameKey.match(/Kopya(\d+)$/);
                        if (cloneMatch) {
                            const num = parseInt(cloneMatch[1], 10);
                            if (num > maxCloneNumber) {
                                maxCloneNumber = num;
                            }
                        } else if (filter.nameKey === baseNameKey + 'Kopya') {
                            if (maxCloneNumber === 0) {
                                maxCloneNumber = 1;
                            }
                        }
                    }
                });

                const nextCloneNumber = maxCloneNumber + 1;
                if (nextCloneNumber === 1) {
                    uniqueName = `${baseName} Kopya 1`;
                } else {
                    uniqueName = `${baseName} Kopya ${nextCloneNumber}`;
                }
            }

            return uniqueName;
        },
        getStatusTitle(value) {
            if (value === "all") return this.$t('chat.filterPanel.all');
            if (value === "open") return this.$t('chat.filterPanel.open');
            if (value === "closed") return this.$t('chat.filterPanel.closed');
            return this.$t('chat.filterPanel.unknown');
        },
        initSearchQuery() {
            const searchField = _.get(this.$route, "query.searchField", null);
            if (searchField) this.selectedField = searchField;
            const searchQuery = _.get(this.$route, "query.q", null);
            if (searchQuery) this.searchQuery = searchQuery;
        },
        initFilters() {
            this.filters = [...(_.get(this.filter, "filters") || this.filters)];
        },
        onFiltersChanged(payload) {
            const filters = _.cloneDeep(payload.filters).filter((filter) => {
                return filter.value !== null && filter.value !== undefined && filter.value !== "";
            });
            const newFilterHash = StringUtils.hashCodeStr(JSON.stringify(filters));
            const oldFilterHash = StringUtils.hashCodeStr(JSON.stringify(this.filters));
            const isStateChanged = this.state !== payload.state;
            const isFiltersChanged = newFilterHash !== oldFilterHash;
            const isSortChanged = this.sortValue !== payload.sort;

            this.state = _.cloneDeep(payload.state);
            this.filters = _.cloneDeep(payload.filters);
            this.sortValue = payload.sort || "relevance";

            // Dropdown'dan sort değiştiğinde tablo sort'unu temizle
            if (isSortChanged) {
                this.sortField = null;
                this.sortDirection = null;
            }

            if (isFiltersChanged || isStateChanged || isSortChanged) {
                this.getContactDebounced(20, true, undefined, true);
            }
        },
        onStateChange(value) {
            this.state = value;
            this.getContactDebounced(20, true, undefined, true);
        },
        goBackToChat() {
            this.setActiveView("chat");
        },
        startEditName() {
            this.tempFilterName = this.filter?.name || "";
            this.editingName = true;
            this.$nextTick(() => {
                if (this.$refs.nameInput) {
                    this.$refs.nameInput.focus();
                }
            });
        },
        onNameInput(event) {
            this.tempFilterName = event.target.value;
        },
        commitEditName() {
            if (!this.editingName) return;
            this.editingName = false;
            if (!this.tempFilterName || this.tempFilterName.trim() === "") {
                this.tempFilterName = null;
            }
        },
        async onSaveFilter() {
            if (this.isSystemFilter && (this.isFiltersChanged || this.hasNameChanged || this.hasIconChanged)) {
                const proposedName = this.tempFilterName ? this.tempFilterName.trim() : this.filter?.name || "";
                this.pendingFilterName = proposedName;
                if (proposedName) {
                    const uniqueName = this.getUniqueFilterName(proposedName);
                    if (uniqueName !== proposedName) {
                        this.pendingSuggestedName = uniqueName;
                    } else {
                        this.pendingSuggestedName = "";
                    }
                } else {
                    this.pendingSuggestedName = "";
                }
                this.pendingFilterDescription = "";
                this.showFilterNameModal = true;
                return;
            }

            if (this.isCustomFilter) {
                let updatingFields = {
                    filters: _.cloneDeep(this.filters),
                    name: this.tempFilterName ? this.tempFilterName.trim() : this.filter?.name || this.$t('chat.filterPanel.unnamedFilter'),
                    icon: this.tempFilterIcon,
                    description:
                        this.filter && this.filter.description !== undefined && this.filter.description !== null
                            ? String(this.filter.description)
                            : "",
                };
                try {
                    const contacts = await this.getContacts(20, true, "all");
                    const total = _.get(contacts, "count", 0);
                    this.loading = false;

                    const filter = await updateFilterMultipleKey(
                        this.filter._id,
                        updatingFields
                    );
                    const filters = this.contactFilters.map((f) => {
                        if (f.id === this.filter._id) {
                            f.filters = filter.filters;
                            f.name = filter.name;
                            f.key = filter.nameKey;
                            f.icon = filter.icon;
                            f.count = total;
                            f.description = filter.description !== undefined && filter.description !== null ? filter.description : "";
                        }
                        return f;
                    });
                    this.updateContactListFilters(filters);
                    this.$router.replace({
                        query: {
                            tab: "inbox",
                            inbox: filter.nameKey,
                            view: "filter",
                        },
                    });
                    this.$notification.success({
                        message: this.$t('chat.filterPanel.filterUpdatedSuccess'),
                        description: this.$t('chat.filterPanel.filterUpdatedDescription', { name: this.filter.name }),
                    });
                } catch (error) {
                    this.loading = false;
                    this.$notification.error({
                        message: this.$t('chat.filterPanel.filterUpdateFailed'),
                        description: error.response?.data?.error || this.$t('chat.filterPanel.filterUpdateError'),
                    });
                }
            }
        },
        async createNewFilter(filterName, filterDescription) {
            try {
                const createName = filterName.trim();
                const createIcon = this.tempFilterIcon || "filter_list";
                const userId = _.get(this.$store, "state.auth.user._id");
                const desc = filterDescription !== undefined && filterDescription !== null ? String(filterDescription) : "";

                const filter = await createFilter(
                    "page.contact-list",
                    createName,
                    _.cloneDeep(this.filters),
                    userId,
                    null,
                    createIcon,
                    this.filterVisibility,
                    null,
                    desc
                );

                const contacts = await this.getContacts(20, true, "all");
                const total = _.get(contacts, "count", 0);
                const filters = [
                    ...this.contactFilters,
                    {
                        type: "custom",
                        id: filter._id,
                        _id: filter._id,
                        name: filter.name,
                        key: filter.nameKey,
                        icon: filter.icon,
                        filters: _.cloneDeep(this.filters),
                        count: total,
                        visibility: filter.visibility,
                        description: filter.description || "",
                    },
                ];
                this.updateContactListFilters(filters);

                this.$router.replace({
                    query: {
                        tab: "inbox",
                        inbox: filter.nameKey,
                        view: "filter",
                    },
                });

                if (filter.name !== createName) {
                    this.$notification.success({
                        message: this.$t('chat.filterPanel.filterSavedSuccess'),
                        description: this.$t('chat.filterPanel.filterSavedDescription', { createName: createName, filterName: filter.name }),
                    });
                } else {
                    this.$notification.success({
                        message: this.$t('chat.filterPanel.filterSavedSuccess'),
                        description: this.$t('chat.filterPanel.filterCreatedDescription', { filterName: filter.name }),
                    });
                }
            } catch (error) {
                const errorMessage = error.response?.data?.error || error.message || this.$t('chat.filterPanel.filterSaveError');
                this.$notification.error({
                    message: this.$t('chat.filterPanel.filterSaveFailed'),
                    description: errorMessage,
                });
            }
        },
        confirmFilterName: async function (payload) {
            this.showFilterNameModal = false;

            let filterName;
            let filterDescription = "";
            if (payload && typeof payload === "object" && payload.name !== undefined) {
                filterName = payload.name;
                filterDescription = payload.description || "";
            } else {
                filterName = payload;
            }

            if (this.pendingFilterData) {
                await this.createFilterFromSuggestions(filterName, filterDescription);
                this.pendingFilterData = null;
            } else {
                await this.createNewFilter(filterName, filterDescription);
            }

            this.pendingFilterName = "";
            this.pendingFilterDescription = "";
            this.pendingSuggestedName = "";
        },
        async createFilterFromSuggestions(filterName, filterDescription) {
            const filterData = this.pendingFilterData;
            const createName = filterName.trim();
            const createIcon = 'auto_awesome';
            const userId = _.get(this.$store, "state.auth.user._id");
            const desc = filterDescription !== undefined && filterDescription !== null ? String(filterDescription) : "";

            try {
                const filter = await createFilter(
                    "page.contact-list",
                    createName,
                    _.cloneDeep(filterData.filters),
                    userId,
                    null,
                    createIcon,
                    'private',
                    null,
                    desc
                );

                const contacts = await this.getContacts(20, true, "all");
                const total = _.get(contacts, "count", 0);

                const filters = [
                    ...this.contactFilters,
                    {
                        type: "custom",
                        id: filter._id,
                        _id: filter._id,
                        name: filter.name,
                        key: filter.nameKey,
                        icon: filter.icon,
                        filters: _.cloneDeep(filterData.filters),
                        count: total,
                        visibility: 'private',
                        description: filter.description || "",
                    },
                ];

                this.updateContactListFilters(filters);

                this.$router.replace({
                    query: {
                        tab: "inbox",
                        inbox: filter.nameKey,
                        view: "filter",
                    },
                });

                if (filter.name !== createName) {
                    this.$notification.success({
                        message: this.$t('chat.filterPanel.aiFilterCreatedSuccess'),
                        description: this.$t('chat.filterPanel.aiFilterCreatedDescription', { createName: createName, filterName: filter.name }),
                    });
                } else {
                    this.$notification.success({
                        message: this.$t('chat.filterPanel.aiFilterCreatedSuccess'),
                        description: this.$t('chat.filterPanel.aiFilterCreatedApplied', { filterName: filter.name }),
                    });
                }
            } catch (error) {
                const errorMessage = error.response?.data?.error || error.message || this.$t('chat.filterPanel.aiFilterCreateError');
                this.$notification.error({
                    message: this.$t('chat.filterPanel.aiFilterCreateFailed'),
                    description: errorMessage,
                });

                this.filters = [...filterData.filters];
                this.getContactDebounced(20, true, undefined, true);
            }
        },
        cancelFilterName: function () {
            this.showFilterNameModal = false;
            this.pendingFilterName = "";
            this.pendingFilterDescription = "";
            this.pendingSuggestedName = "";
        },
        updateSuggestedName: function (filterName) {
            if (filterName && filterName.trim()) {
                const uniqueName = this.getUniqueFilterName(filterName.trim());
                if (uniqueName !== filterName.trim()) {
                    this.pendingSuggestedName = uniqueName;
                } else {
                    this.pendingSuggestedName = "";
                }
            } else {
                this.pendingSuggestedName = "";
            }
        },
        searchContacts() {
            if (this.selectedField !== 'all' || !this.searchQuery || !this.searchQuery.trim()) {
                // Search temizlendiğinde de tablo sort'unu temizle
                this.sortField = null;
                this.sortDirection = null;
                this.getContactDebounced(20, true);
                return;
            }

            const query = this.searchQuery.trim();

            if (this.isPhoneNumber(query)) {
                this.detectedSearchMode = 'phone';
                this.pendingSearchQuery = query;
                this.showSearchModeModal = true;
                return;
            }

            if (this.isEmail(query)) {
                this.detectedSearchMode = 'email';
                this.pendingSearchQuery = query;
                this.showSearchModeModal = true;
                return;
            }

            if (this.isMongoId(query)) {
                this.detectedSearchMode = 'mongoId';
                this.pendingSearchQuery = query;
                this.showSearchModeModal = true;
                return;
            }

            // Search yapılırken tablo sort'unu temizle
            this.sortField = null;
            this.sortDirection = null;
            this.getContactDebounced(20, true);
        },
        isPhoneNumber(value) {
            const digitCount = (value.match(/\d/g) || []).length;
            if (digitCount < 8) return false;

            const phonePattern = /^[\+]?[\d\s\-\(\)\.]+$/;
            if (!phonePattern.test(value)) return false;

            if (value.includes('@')) return false;
            if (value.includes('http')) return false;

            return true;
        },
        isEmail(value) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailPattern.test(value);
        },
        isMongoId(value) {
            const mongoIdPattern = /^[a-f\d]{24}$/i;
            return mongoIdPattern.test(value);
        },
        confirmSearchMode() {
            if (this.detectedSearchMode === 'mongoId') {
                if (!this.mongoIdSelectedType) {
                    return;
                }
                this.selectedField = this.mongoIdSelectedType;
            } else if (this.detectedSearchMode === 'phone') {
                this.selectedField = 'phone';
            } else if (this.detectedSearchMode === 'email') {
                this.selectedField = 'email';
            } else if (this.detectedSearchMode === 'contactId') {
                this.selectedField = 'contactId';
            } else if (this.detectedSearchMode === 'conversationId') {
                this.selectedField = 'conversationId';
            }

            let cleanedQuery = this.pendingSearchQuery;
            if (this.detectedSearchMode === 'phone') {
                cleanedQuery = cleanedQuery.replace(/\D/g, '');
            }

            this.searchQuery = cleanedQuery;
            this.showSearchModeModal = false;
            this.pendingSearchQuery = null;
            this.detectedSearchMode = null;
            this.mongoIdSelectedType = null;

            // Search yapılırken tablo sort'unu temizle
            this.sortField = null;
            this.sortDirection = null;
            this.getContactDebounced(20, true, undefined, true);
        },
        selectMongoIdType(type) {
            this.mongoIdSelectedType = type;
        },
        cancelSearchMode() {
            const query = this.pendingSearchQuery;

            this.showSearchModeModal = false;
            this.pendingSearchQuery = null;
            this.detectedSearchMode = null;
            this.mongoIdSelectedType = null;

            this.searchQuery = query;

            // Search yapılırken tablo sort'unu temizle
            this.sortField = null;
            this.sortDirection = null;
            this.getContactDebounced(20, true, undefined, true);
        },
        handleKeyDown(event) {
            if (this.showSearchModeModal && event.key === 'Enter') {
                event.preventDefault();
                this.confirmSearchMode();
            }
        },
        getSearchModeIcon() {
            if (this.detectedSearchMode === 'phone') return 'phone';
            if (this.detectedSearchMode === 'email') return 'email';
            if (this.detectedSearchMode === 'mongoId') return 'fingerprint';
            if (this.detectedSearchMode === 'contactId') return 'person';
            if (this.detectedSearchMode === 'conversationId') return 'chat';
            return 'info';
        },
        getSearchModeTitle() {
            if (this.detectedSearchMode === 'phone') return this.$t('chat.filterPanel.phoneDetected');
            if (this.detectedSearchMode === 'email') return this.$t('chat.filterPanel.emailDetected');
            if (this.detectedSearchMode === 'mongoId') return this.$t('chat.filterPanel.idFormatDetected');
            if (this.detectedSearchMode === 'contactId') return this.$t('chat.filterPanel.contactIdDetected');
            if (this.detectedSearchMode === 'conversationId') return this.$t('chat.filterPanel.conversationIdDetected');
            return this.$t('chat.filterPanel.searchMode');
        },
        getSearchModeMessage() {
            if (this.detectedSearchMode === 'phone') {
                return this.$t('chat.filterPanel.phoneDetectedMessage');
            }
            if (this.detectedSearchMode === 'email') {
                return this.$t('chat.filterPanel.emailDetectedMessage');
            }
            if (this.detectedSearchMode === 'mongoId') {
                return this.$t('chat.filterPanel.idFormatDetectedMessage');
            }
            return '';
        },
        getSearchModeFieldName() {
            if (this.detectedSearchMode === 'phone') return this.$t('chat.filterPanel.phoneField');
            if (this.detectedSearchMode === 'email') return this.$t('chat.filterPanel.emailField');
            if (this.detectedSearchMode === 'contactId') return this.$t('chat.filterPanel.contactIdField');
            if (this.detectedSearchMode === 'conversationId') return this.$t('chat.filterPanel.conversationIdFieldName');
            if (this.detectedSearchMode === 'mongoId') return this.$t('chat.filterPanel.idField');
            return '';
        },
        clearSearch() {
            this.searchQuery = "";
            // Search temizlendiğinde tablo sort'unu da temizle
            this.sortField = null;
            this.sortDirection = null;
            this.getContactDebounced(20, true, undefined, true);
        },
        onIconSelected(iconName) {
            this.tempFilterIcon = iconName;
        },
        normalizeFilters(filters) {
            const arr = _.cloneDeep(filters || []);
            return _.sortBy(arr, (item) => item.field);
        },
        getContacts(limit = 20, resetCursor = false, state = null) {
            this.loading = true;

            if (resetCursor) {
                this.cursor.nextToken = null;
                this.cursor.prevToken = null;
                this.cursor.firstPageToken = null;
            }

            let payload = {
                metadata: {
                    source: "filter-panel",
                },
                cursor: {
                    limit,
                },
                state: state || this.state,
            };
            if (this.cursor.nextToken) {
                payload.cursor.nextToken = this.cursor.nextToken;
            }
            if (this.cursor.prevToken) {
                payload.cursor.prevToken = this.cursor.prevToken;
            }
            if (this.cursor.firstPageToken) {
                payload.cursor.firstPageToken = this.cursor.firstPageToken;
            }
            if (this.filters.length > 0) {
                payload.filters = this.filters.filter((filter) => {
                    return filter.value !== null && filter.value !== undefined && filter.value !== "";
                });
            }
            if (this.searchQuery) {
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        searchField: this.selectedField,
                        q: this.searchQuery,
                    },
                });
                payload.search = {
                    field: this.selectedField,
                    query: this.searchQuery,
                };
            }

            // Sort: Önce tablo sort'una bak, yoksa dropdown sort'unu kullan
            if (this.sortField && this.sortDirection) {
                // Tablo sort'u varsa onu kullan
                payload.sort = {
                    field: this.sortField,
                    direction: this.sortDirection,
                };
            } else if (this.sortValue && this.sortValue !== "relevance") {
                // Tablo sort'u yoksa dropdown sort'unu kullan
                const [field, direction] = this.sortValue.split("_");
                payload.sort = {
                    field: field,
                    direction: direction.toUpperCase(),
                };
            }

            return this.fetchContacts(payload);
        },
        getContactDebounced(limit = 20, resetCursor = false, state = null, stopTimer = false) {
            if (this.contactTimer) clearTimeout(this.contactTimer);
            this.contactTimer = setTimeout(
                () => {
                    this.getContacts(limit, resetCursor, state).then((data) => {
                        if (!this.cursor.nextToken && !this.cursor.prevToken) {
                            this.totalContacts = _.get(data, "count", 0);
                        }
                        this.contacts = _.get(data, "contacts", []);
                        this.cursor.nextToken = _.get(data, "cursor.nextToken", null);
                        this.cursor.prevToken = _.get(data, "cursor.prevToken", null);
                        this.cursor.firstPageToken =
                            _.get(data, "cursor.firstPageToken", null) || this.cursor.firstPageToken;
                        this.loading = false;
                        return data;
                    });
                },
                stopTimer ? 0 : 400
            );
        },
        goToNextPage() {
            if (!this.cursor.nextToken || this.loading) {
                return;
            }
            this.cursor.prevToken = null;
            this.getContactDebounced(undefined, undefined, undefined, true);
        },
        goToPrevPage() {
            if (!this.cursor.prevToken || this.loading) {
                return;
            }
            this.cursor.nextToken = null;
            this.getContactDebounced(undefined, undefined, undefined, true);
        },
        async changeVisibility(visibility) {
            try {
                await updateFilterMultipleKey(
                    this.filter._id,
                    { visibility }
                );

                const filters = this.contactFilters.map((f) => {
                    if (f.id === this.filter._id) {
                        f.visibility = visibility;
                    }
                    return f;
                });
                this.updateContactListFilters(filters);

                const visibilityText = visibility === "public" ? this.$t('chat.filterPanel.publicVisibility') : this.$t('chat.filterPanel.privateVisibility');
                this.$notification.success({
                    message: this.$t('chat.filterPanel.visibilityUpdatedSuccess'),
                    description: this.$t('chat.filterPanel.visibilityUpdatedDescription', { visibility: visibilityText }),
                });
            } catch (error) {
                this.$notification.error({
                    message: this.$t('chat.filterPanel.visibilityUpdateFailed'),
                    description: this.$t('chat.filterPanel.visibilityUpdateError'),
                });
            }
        },
        async deleteFilter() {
            this.$confirm({
                title: this.$t('chat.filterPanel.deleteFilter'),
                content: this.$t('chat.filterPanel.deleteFilterConfirm', { name: this.filter.name }),
                okText: this.$t('chat.filterPanel.delete'),
                okType: "danger",
                cancelText: this.$t('chat.filterPanel.cancel'),
                onOk: async () => {
                    try {
                        await deleteFilterApi(this.filter._id);
                        this.$notification.success({
                            message: this.$t('chat.filterPanel.filterDeletedSuccess'),
                            description: this.$t('chat.filterPanel.filterDeletedDescription', { name: this.filter.name }),
                        });

                        this.$router.replace({
                            query: {
                                tab: "inbox",
                                inbox: "all",
                                view: "filter",
                            },
                        });
                        const filters = this.contactFilters.filter((f) => f.id !== this.filter._id);
                        this.updateContactListFilters(filters);
                    } catch (error) {
                        this.$notification.error({
                            message: this.$t('chat.filterPanel.filterDeleteFailed'),
                            description: this.$t('chat.filterPanel.filterDeleteError'),
                        });
                    }
                },
            });
        },
        openFilterSuggestionsModal() {
            this.showFilterSuggestionsModal = true;
        },
        closeFilterSuggestionsModal() {
            this.showFilterSuggestionsModal = false;
        },
        async applyFiltersFromSuggestions(filterData) {
            this.closeFilterSuggestionsModal();

            // Eğer sadece filtreler geldiyse (eski format), direkt uygula
            if (Array.isArray(filterData)) {
                this.filters = [...filterData];
                this.getContactDebounced(20, true, undefined, true);
                return;
            }

            if (filterData.shouldCreateFilter) {
                const proposedName = filterData.name || '';
                this.pendingFilterName = proposedName;
                if (proposedName) {
                    const uniqueName = this.getUniqueFilterName(proposedName);
                    if (uniqueName !== proposedName) {
                        this.pendingSuggestedName = uniqueName;
                    } else {
                        this.pendingSuggestedName = "";
                    }
                } else {
                    this.pendingSuggestedName = "";
                }
                this.pendingFilterDescription = "";
                this.showFilterNameModal = true;
                this.pendingFilterData = filterData;
                return;
            } else {
                this.filters = [...filterData.filters];
                this.getContactDebounced(20, true, undefined, true);
            }
        },
        transformFiltersToTable: function(filters) {
            // FilterPanel formatını (obje) NewSupsisTable formatına (basit) çevir
            return filters.map(function(filter) {
                const transformedFilter = {
                    field: filter.field,
                    value: filter.value,
                    operator: filter.operator
                };
                
                // owner field'ı için: obje → basit string
                if (filter.field === 'owner' && Array.isArray(filter.value)) {
                    transformedFilter.value = filter.value.map(function(val) {
                        if (typeof val === 'object' && val !== null && val.value) {
                            return val.value;
                        }
                        return val;
                    });
                }
                // lastConversationUsers field'ı için: obje → basit string
                else if (filter.field === 'lastConversationUsers' && Array.isArray(filter.value)) {
                    transformedFilter.value = filter.value.map(function(val) {
                        if (typeof val === 'object' && val !== null && val.value) {
                            return val.value;
                        }
                        return val;
                    });
                }
                
                return transformedFilter;
            });
        },
        onFilterToggle: function (payload) {
            const isMultiple = payload.isMultiple || false;
            
            // Basit formatı (NewSupsisTable'dan gelen) obje formatına çevir
            const transformToObjFormat = function(val, field) {
                // owner field'ı için
                if (field === 'owner') {
                    if (val === 'unowned' || val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
                        return { type: 'unowned', value: 'unowned' };
                    }
                    return { type: 'agent', value: val };
                }
                // lastConversationUsers field'ı için
                if (field === 'lastConversationUsers') {
                    if (val === 'none' || val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
                        return { type: 'none', value: 'none' };
                    }
                    return { type: 'agent', value: val };
                }
                // Diğer fieldlar için olduğu gibi dön
                return val;
            };
            
            // Obje karşılaştırma
            const isSameValue = function(v1, v2) {
                if (typeof v1 === 'object' && v1 !== null && typeof v2 === 'object' && v2 !== null) {
                    return v1.value === v2.value;
                }
                return v1 === v2;
            };

            // ACTION: REMOVE
            if (payload.action === 'remove') {
                const existingFilter = this.filters.find(f => f.field === payload.field);
                
                if (existingFilter) {
                    if (isMultiple && Array.isArray(existingFilter.value)) {
                        let valuesToRemove = Array.isArray(payload.value) ? payload.value : [payload.value];
                        valuesToRemove = valuesToRemove.map(function(val) {
                            return transformToObjFormat(val, payload.field);
                        });
                        
                        const newArray = existingFilter.value.filter(function(v) {
                            return !valuesToRemove.some(function(vr) {
                                return isSameValue(v, vr);
                            });
                        });
                        
                        if (newArray.length === 0) {
                            this.filters = this.filters.filter(f => f.field !== payload.field);
                        } else {
                            existingFilter.value = newArray;
                        }
                    } else {
                        this.filters = this.filters.filter(f => f.field !== payload.field);
                    }
                }
            }
            // ACTION: ADD
            else if (payload.action === 'add') {
                const existingFilter = this.filters.find(f => f.field === payload.field);
                let newFilterValue = payload.value;
                
                if (isMultiple) {
                    let valuesToAdd = Array.isArray(payload.value) ? payload.value : [payload.value];
                    
                    valuesToAdd = valuesToAdd.map(function(val) {
                        return transformToObjFormat(val, payload.field);
                    });
                    
                    if (existingFilter && Array.isArray(existingFilter.value)) {
                        const newValues = valuesToAdd.filter(function(v) {
                            return !existingFilter.value.some(function(ev) {
                                return isSameValue(v, ev);
                            });
                        });
                        if (newValues.length > 0) {
                            newFilterValue = existingFilter.value.concat(newValues);
                        } else {
                            return;
                        }
                    } else if (existingFilter && !Array.isArray(existingFilter.value)) {
                        newFilterValue = [existingFilter.value].concat(valuesToAdd);
                    } else {
                        newFilterValue = [].concat(valuesToAdd);
                    }
                } else {
                    newFilterValue = transformToObjFormat(payload.value, payload.field);
                }
                
                const newFilter = {
                    field: payload.field,
                    value: newFilterValue
                };

                if (payload.operator) {
                    newFilter.operator = payload.operator;
                }

                if (existingFilter) {
                    existingFilter.value = newFilterValue;
                    if (payload.operator) {
                        existingFilter.operator = payload.operator;
                    }
                } else {
                    this.filters.push(newFilter);
                }
            }
            
            // Verileri yeniden yükle
            this.getContactDebounced(20, true, undefined, true);
        },
        onSortChange: function (sortPayload) {
            // sortPayload: { key: 'columnKey', order: 'asc' | 'desc' | null }
            if (!sortPayload.key || !sortPayload.order) {
                // Tablo sort'u kaldırıldı - dropdown sort'a dön
                this.sortField = null;
                this.sortDirection = null;
            } else {
                // Tablo sort'u uygulandı - dropdown sort'unu geçersiz kıl
                this.sortField = sortPayload.key;
                this.sortDirection = sortPayload.order.toUpperCase(); // 'asc' -> 'ASC', 'desc' -> 'DESC'
            }

            // Verileri yeniden yükle
            this.getContactDebounced(20, true, undefined, true);
        },
    },
    mounted() {
        this.tempFilterIcon = this.filter?.icon;
        this.initFilters();
        this.initSearchQuery();

        if (this.filterId || this.filters.length > 0 || this.searchQuery) {
            this.getContactDebounced(20, true, undefined, true);
        }

        window.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handleKeyDown);
    },
};
</script>
<style lang="scss">
.filter-page {
    width: 100%;
    height: 100%;
    padding: 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .state-selector {
        .chip {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            padding: 0.35rem 0.7rem;
            border: 1px solid #c7d0d7;
            border-radius: 999px;
            background: #fff;
            color: #22272d;
            cursor: pointer;
            user-select: none;
            transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
            font-weight: 600;

            &:hover {
                background: #f0f0f0;
                color: #22272d;

                .material-icons {
                    color: #22272d !important;
                }
            }

            .material-icons {
                font-size: 18px;
                line-height: 1;
                color: #22272d;
            }
        }
    }

    .filter-content-wrapper {
        margin-bottom: 5px;
    }

    .contact-count {
        display: flex;
        align-items: flex-start;
        padding: 0;
        margin-top: 2px;
        flex-shrink: 0;
        max-width: 100%;

        .count-text {
            color: #6b7785;
            font-size: 11px;
            font-weight: 400;
            letter-spacing: 0.01em;
            white-space: nowrap;
            line-height: 1.4;
        }
    }

    .filter-page-header {
        margin-bottom: 10px;
        flex-shrink: 0;
    }

    .filter-header-content {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 18px;
    }

    .filter-title-section {
        display: flex;
        align-items: flex-start;
        gap: 12.6px;
        flex: 1;
        min-width: 0;

        .back-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border: none;
            background: transparent;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
            flex-shrink: 0;
            color: #6b7785;

            &:hover {
                background: #f5f7fa;
                color: #22272d;
            }

            &:active {
                background: #e5e7eb;
            }

            .material-icons {
                font-size: 24px;
            }
        }
    }

    .filter-icon-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
    }

    .filter-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        background: #f0f0f0;
        border-radius: 7.2px;
        transition: all 0.2s ease;

        &.editable {
            cursor: pointer;

            &:hover {
                background: #e0e0e0;
                transform: scale(1.05);
            }
        }

        .material-icons {
            font-size: 18px;
            color: #22272d;
        }
    }

    .filter-title-text {
        display: flex;
        flex-direction: column;
    }

    .filter-name-container {
        display: flex;
        align-items: center;
        gap: 7.2px;

        &.editable {
            cursor: pointer;

            &:hover {
                .filter-name {
                    color: #22272d;
                    text-decoration: underline;
                }
            }
        }

        .visibility-icon {
            font-size: 14.4px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6b7785;
        }
    }

    .filter-name {
        font-size: 16.2px;
        font-weight: 600;
        color: #22272d;
        transition: color 0.2s ease;
    }

    .edit-name-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        background: transparent;
        border: none;
        border-radius: 3.6px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            background: #f0f0f0;
        }

        .material-icons {
            font-size: 12.6px;
            color: #6b7785;
        }
    }

    .edit-name-container {
        margin-top: 1.8px;
    }

    .filter-name-input {
        height: 32.4px;
        padding: 0 10.8px;
        border: 2px solid #22272d;
        border-radius: 5.4px;
        font-size: 14.4px;
        font-weight: 600;
        color: #22272d;
        background: #fff;
        outline: none;
        min-width: 180px;

        &::placeholder {
            color: #bfbfbf;
            font-weight: 400;
        }
    }

    .filter-actions {
        display: flex;
        align-items: flex-start;
        gap: 10.8px;
        padding-top: 6px;
        flex-shrink: 0;

        .filter-suggestions-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7.2px;
            height: 32.4px;
            padding: 0 14.4px;
            border: 1px solid transparent;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 7.2px;
            transition: all 0.3s ease;

            &:hover {
                background: linear-gradient(135deg, #5568d3 0%, #6a3f91 100%);
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
            }

            .material-icons {
                font-size: 16.2px;
            }
        }

        .visibility-selector {
            display: inline-flex;
            align-items: center;
            gap: 1.8px;
            padding: 1.8px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 7.2px;

            .visibility-option {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 5.4px;
                padding: 6.3px 12.6px;
                border-radius: 5.4px;
                cursor: pointer;
                font-size: 11.7px;
                font-weight: 500;
                color: #6c757d;
                background: transparent;
                border: none;
                outline: none;
                transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                white-space: nowrap;
                user-select: none;
                margin-bottom: 0;

                input[type="radio"] {
                    display: none;
                }

                .material-icons {
                    font-size: 14.4px;
                    color: currentColor;
                }

                &:hover:not(.active) {
                    background: rgba(0, 0, 0, 0.04);
                    color: #495057;
                }

                &.active {
                    background: #ffffff;
                    color: #212529;
                    font-weight: 600;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
                }
            }
        }

        .save-btn {
            display: inline-flex;
            align-items: center;
            gap: 5.4px;
            padding: 0 14.4px;
            height: 32.4px;

            .material-icons {
                font-size: 16.2px;
            }
        }

        .filter-menu-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 32.4px;
            height: 32.4px;
            padding: 0;
            background: #ffffff;
            border: 1px solid #e9ecef;
            border-radius: 7.2px;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background: #f8f9fa;
                border-color: #dee2e6;
            }

            &:active {
                transform: scale(0.95);
            }

            .material-icons {
                font-size: 18px;
                color: #6c757d;
            }
        }
    }

    .search-section {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 48px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 0 12px 0px 3px;
        background: #fff;
        flex-shrink: 0;
        margin-bottom: 12px;
    }


    .search-container {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    .search-icon-wrapper {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        .search-icon {
            font-size: 18px;
            color: #6b7785;
        }
    }

    .search-field-selector {
        flex-shrink: 0;
        margin-right: 8px;

        .select-button {
            white-space: nowrap;
            width: 140px;
            height: 40px;
            border: none;
            border-radius: 0;
            background: transparent;
        }
    }

    .search-input-wrapper {
        display: flex;
        align-items: center;
        flex: 1;
    }

    .search-input {
        flex: 1;
        height: 36px;
        border: none;
        outline: none;
        background: transparent;
        font-size: 14px;
        color: #22272d;

        &::placeholder {
            color: #bfbfbf;
        }
    }

    .clear-search-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background: transparent;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background 0.2s ease;

        &:hover {
            background: #f0f0f0;
        }

        .material-icons {
            font-size: 16px;
            color: #6b7785;
        }
    }

    .contact-list-container {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;

        .supsis-col-avatar {
            .supsis-cell-content {
                padding: 0px;
                justify-content: center;
                align-items: center;
            }
        }
    }

    .pagination-controls {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 7px;
        margin-top: 12px;
        padding: 0;
    }

    .pagination-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 5px 12px;
        height: 28px;
        min-width: 70px;
        justify-content: center;
        font-size: 13px;

        .material-icons {
            font-size: 15px;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &.prev-btn {
            .material-icons {
                order: -1;
            }
        }

        &.next-btn {
            .material-icons {
                order: 1;
            }
        }
    }
}

.filter-menu-popover {
    .ant-popover-inner-content {
        padding: 4px !important;
    }

    .modern-custom-dropdown {
        min-width: 220px;
    }

    .filter-menu-content {
        padding: 4px 0;

        .filter-menu-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 14px;
            cursor: pointer;
            transition: background 0.15s ease;
            font-size: 14px;
            color: #22272d;
            border-radius: 4px;
            margin: 0 4px;

            &:hover {
                background: #f5f7fa;
            }

            &.danger {
                color: #ef4444;

                .material-icons {
                    color: #ef4444;
                }

                &:hover {
                    background: #fef2f2;
                }
            }

            .material-icons {
                font-size: 18px;
                color: #6b7785;
            }

            span {
                flex: 1;
                font-weight: 500;
            }
        }

        .filter-menu-divider {
            height: 1px;
            background: #e5e7eb;
            margin: 6px 8px;
        }
    }
}

.search-mode-confirmation-modal {
    .search-mode-modal-title {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 18px;
        font-weight: 600;
        color: #22272d;

        .material-icons {
            font-size: 24px;
            color: #667eea;
        }
    }

    .search-mode-modal-content {
        padding: 20px 0;

        p {
            font-size: 14px;
            color: #6b7785;
            line-height: 1.6;
            margin-bottom: 16px;
        }

        .search-query-preview {
            padding: 12px 16px;
            background: #f8f9fa;
            border-radius: 8px;
            border-left: 3px solid #667eea;
            font-size: 14px;
            color: #22272d;
            margin-bottom: 16px;

            strong {
                font-weight: 600;
                margin-right: 8px;
            }
        }

        .mongo-id-selection {
            margin-top: 20px;

            .selection-label {
                font-size: 14px;
                font-weight: 600;
                color: #22272d;
                margin-bottom: 12px;
            }

            .id-type-buttons {
                display: flex;
                gap: 12px;

                .id-type-btn {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    height: 44px;
                    font-size: 14px;
                    font-weight: 500;

                    .material-icons {
                        font-size: 20px;
                    }
                }
            }
        }
    }

    .search-mode-modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding-top: 16px;
        border-top: 1px solid #f0f0f0;
    }
}

</style>
